// Client side of the pre-admission WS (SPEC §5.4 / F-LOB-2). The admitted
// envelope is the ONLY place a guest ever receives the room token and the
// Model A session secret.
//
// Liveness: the server emits a JSON "hb" envelope every 30s (protocol pings
// are invisible to browser JS). If nothing arrives for STALE_MS the socket is
// presumed half-dead (mobile network transitions, proxies) and force-closed,
// which triggers the normal reconnect path — no more silently missed knocks.

import type { JoinGrant } from './api';

function wsBase(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/v1/realtime`;
}

// Server heartbeats every 30s; call the stream dead after missing two.
const STALE_MS = 75_000;
const STALE_CHECK_MS = 15_000;

/** Force-close `ws` when no message has arrived within STALE_MS. Returns a
 * stop function. Closing fires onclose, which owns reconnection. */
function watchStaleness(ws: WebSocket, lastSeen: () => number): () => void {
  const timer = setInterval(() => {
    if (Date.now() - lastSeen() > STALE_MS) ws.close();
  }, STALE_CHECK_MS);
  return () => clearInterval(timer);
}

export type KnockOutcome =
  | { kind: 'admitted'; grant: JoinGrant }
  | { kind: 'denied' }
  | { kind: 'expired' };

export interface KnockWatch {
  outcome: Promise<KnockOutcome>;
  cancel: () => void;
}

/** Wait on a knock. onWaiting reports host presence for the two waiting
 * messages (§6.2). Reconnects on transient socket loss until decided. */
export function watchKnock(knockID: string, onWaiting: (hostPresent: boolean) => void): KnockWatch {
  let ws: WebSocket | null = null;
  let cancelled = false;
  let attempts = 0;

  const outcome = new Promise<KnockOutcome>((resolve) => {
    const open = () => {
      if (cancelled) return;
      const sock = new WebSocket(`${wsBase()}?knock=${encodeURIComponent(knockID)}`);
      ws = sock;
      let decided = false;
      let lastMsg = Date.now();
      const stopStale = watchStaleness(sock, () => lastMsg);

      sock.onmessage = (ev) => {
        lastMsg = Date.now();
        let env: { t: string; hostPresent?: boolean; grant?: JoinGrant };
        try {
          env = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        switch (env.t) {
          case 'waiting':
            attempts = 0;
            onWaiting(env.hostPresent ?? false);
            break;
          case 'admitted':
            decided = true;
            resolve({ kind: 'admitted', grant: env.grant! });
            break;
          case 'denied':
            decided = true;
            resolve({ kind: 'denied' });
            break;
          case 'expired':
            decided = true;
            resolve({ kind: 'expired' });
            break;
          // 'hb' needs no handling — receiving it already refreshed lastMsg.
        }
      };
      sock.onclose = () => {
        stopStale();
        if (decided || cancelled) return;
        // Transient drop while still waiting — reconnect (the backend
        // auto-admits on reconnect if the decision landed meanwhile, §5.5).
        attempts += 1;
        if (attempts > 5) {
          resolve({ kind: 'expired' });
          return;
        }
        setTimeout(open, Math.min(1000 * attempts, 5000));
      };
    };
    open();
  });

  return {
    outcome,
    cancel: () => {
      cancelled = true;
      ws?.close();
    },
  };
}

export interface HostQueueHandlers {
  onKnock: (k: { id: string; name: string; at: string }) => void;
  onKnockGone: (id: string, reason: string) => void;
}

/** Host-side admission queue stream; reconnects while the call lasts. The
 * server replays all still-pending knocks on every (re)connect, so a
 * reconnect fully re-syncs the queue. */
export function watchHostQueue(slug: string, handlers: HostQueueHandlers): () => void {
  let ws: WebSocket | null = null;
  let closed = false;

  const open = () => {
    if (closed) return;
    const sock = new WebSocket(`${wsBase()}?host=${encodeURIComponent(slug)}`);
    ws = sock;
    let lastMsg = Date.now();
    const stopStale = watchStaleness(sock, () => lastMsg);

    sock.onmessage = (ev) => {
      lastMsg = Date.now();
      let env: {
        t: string;
        knock?: { id: string; name: string; at: string };
        id?: string;
        reason?: string;
      };
      try {
        env = JSON.parse(ev.data as string);
      } catch {
        return;
      }
      if (env.t === 'knock' && env.knock) handlers.onKnock(env.knock);
      if (env.t === 'knock_gone' && env.id) handlers.onKnockGone(env.id, env.reason ?? '');
    };
    sock.onclose = () => {
      stopStale();
      if (!closed) setTimeout(open, 2000);
    };
  };
  open();

  return () => {
    closed = true;
    ws?.close();
  };
}
